import { FC, FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { ArrowBack, CloudDownload } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Grid, Link, Skeleton, Stack, TextField, Typography } from '@mui/material'

import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useCreateOnboardingVideoMutation, useGetOnboardingVideosQuery } from '../../api/onboarding-videos'
import { useLazyGetVimeoOEmbedQuery } from '../../api/vimeo'
import {
  findVideoWithVimeoId,
  formatVideoDuration,
  getUnstoredOnboardingVideoFields,
  onboardingVideoFieldLabelKeys,
  toOnboardingVideoCreateBody,
} from '../../features/onboarding-videos/helpers/onboardingVideoHelpers'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { parseVimeoId } from '../../helpers/vimeo'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { onboardingVideoEditPath, onboardingVideosPath } from './OnboardingVideosPage'

const errorStatus = (error: unknown) => (typeof error === 'object' && error !== null && 'status' in error ? (error as { status: unknown }).status : undefined)

export const OnboardingVideoAddPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-onboarding-video-add'))
  const { t } = useTranslation()
  const { notify, notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const videosQuery = useGetOnboardingVideosQuery()
  const [loadOEmbed, oembedResult] = useLazyGetVimeoOEmbedQuery()
  const [createVideo, { isLoading: isCreating }] = useCreateOnboardingVideoMutation()
  const [input, setInput] = useState('')
  const [touched, setTouched] = useState(false)

  const vimeoId = parseVimeoId(input)
  const oembed = oembedResult.isSuccess && oembedResult.originalArgs === vimeoId ? oembedResult.data : undefined
  const createBody = oembed && toOnboardingVideoCreateBody(oembed)
  const existing = vimeoId ? findVideoWithVimeoId(videosQuery.data, vimeoId) : undefined

  const handleLoad = (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (vimeoId) loadOEmbed(vimeoId)
  }

  const oembedErrorKey = () => {
    const status = errorStatus(oembedResult.error)
    if (status === 404) return 'internal-csm:onboarding_videos_vimeo_not_found'
    if (status === 403) return 'internal-csm:onboarding_videos_vimeo_restricted'
    return 'internal-csm:onboarding_videos_vimeo_failed'
  }

  const handleCreate = async () => {
    if (!createBody) return
    const name = createBody.names.en
    const listedBefore = videosQuery.data
    try {
      const stored = await createVideo(createBody).unwrap()
      const unstored = getUnstoredOnboardingVideoFields(createBody, stored)
      if (unstored.length) {
        const fields = unstored.map((field) => t(onboardingVideoFieldLabelKeys[field])).join(', ')
        notifyError(t('internal-csm:onboarding_videos_not_stored', { fields }))
      } else {
        notifySuccess(t('internal-csm:onboarding_videos_created', { name }))
      }
      navigate(onboardingVideoEditPath(stored.id))
    } catch (error) {
      const listed =
        errorStatus(error) === 409
          ? undefined
          : await videosQuery
              .refetch()
              .unwrap()
              .catch(() => undefined)
      const created = findVideoWithVimeoId(listed, createBody.vimeoId)
      if (created && !findVideoWithVimeoId(listedBefore, createBody.vimeoId)) {
        notify(t('internal-csm:onboarding_videos_created_unconfirmed', { name }), 'warning')
        navigate(onboardingVideoEditPath(created.id))
        return
      }
      const reason = getValidationErrorMessage(error)
      notifyError(reason ? t('internal-csm:onboarding_videos_create_failed_reason', { reason }) : t('internal-csm:onboarding_videos_create_failed'))
    }
  }

  const previewThumbnail = createBody?.thumbnails[3]

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(onboardingVideosPath)}>
            {t('internal-common:back')}
          </Button>
        </Stack>
        <Button variant="contained" onClick={handleCreate} disabled={!createBody || !!existing || isCreating || !videosQuery.isSuccess}>
          {t('internal-csm:onboarding_videos_create')}
        </Button>
      </Stack>

      <Box component="form" onSubmit={handleLoad} noValidate>
        <Stack direction="row" spacing={2} alignItems="flex-start" mb={2}>
          <TextField
            size="small"
            label={t('internal-csm:onboarding_videos_vimeo_input')}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onBlur={() => setTouched(true)}
            error={touched && !!input && !vimeoId}
            helperText={
              touched && !!input && !vimeoId ? t('internal-csm:onboarding_videos_vimeo_input_invalid') : t('internal-csm:onboarding_videos_vimeo_input_help')
            }
            sx={{ minWidth: 420 }}
          />
          <Button type="submit" variant="outlined" startIcon={<CloudDownload />} disabled={!vimeoId || oembedResult.isFetching}>
            {t('internal-csm:onboarding_videos_vimeo_load')}
          </Button>
        </Stack>
      </Box>

      {existing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('internal-csm:onboarding_videos_vimeo_in_use', { name: existing.names?.en || '---' })}{' '}
          <Link component={RouterLink} to={onboardingVideoEditPath(existing.id)}>
            {t('internal-csm:onboarding_videos_open_existing')}
          </Link>
        </Alert>
      )}

      {oembedResult.isFetching ? (
        <Skeleton variant="rectangular" height={320} />
      ) : oembedResult.isError && oembedResult.originalArgs === vimeoId ? (
        <Alert severity="error">{t(oembedErrorKey(), { id: oembedResult.originalArgs })}</Alert>
      ) : (
        oembed &&
        createBody && (
          <Card>
            <CardContent>
              <Grid container spacing={3} wrap="wrap">
                <Grid item xs={12} md={7}>
                  <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9' }}>
                    <Box
                      component="iframe"
                      title={oembed.title}
                      src={`https://player.vimeo.com/video/${oembed.vimeoId}`}
                      allow="autoplay; fullscreen; picture-in-picture"
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6">{oembed.title || '---'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('internal-csm:onboarding_videos_vimeo_details', { id: oembed.vimeoId, duration: formatVideoDuration(oembed.durationSeconds) })}
                    </Typography>
                    <Typography variant="subtitle2">{t('internal-csm:onboarding_videos_thumbnail')}</Typography>
                    {previewThumbnail && <Box component="img" src={previewThumbnail.link} alt="" sx={{ display: 'block', width: '100%' }} />}
                    <Typography variant="body2" color="text.secondary">
                      {t('internal-csm:onboarding_videos_thumbnail_current_note')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('internal-csm:onboarding_videos_create_note')}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )
      )}
    </Box>
  )
}
